import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function axiosHeaders() {
  return {
    "x-loginuserid": localStorage.getItem("userId") || localStorage.getItem("loginUserId") || "",
  };
}

const EmployeeTransferApprovalListing = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const screenBadge = useMemo(() => "INFRA SCREEN : C-25", []);

  // meta
  const [fromSites, setFromSites] = useState([]);
  const [toSites, setToSites] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [defaultStatus, setDefaultStatus] = useState("Waiting");

  // filters
  const [fromSite, setFromSite] = useState("Select");
  const [toSite, setToSite] = useState("Select");
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("Waiting");

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection for approve
  const [selectedEmpIds, setSelectedEmpIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Custom Confirm Modal
  const [showConfirm, setShowConfirm] = useState(false);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const approveBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedEmpIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700");

  const normalizeBranches = (arr) => {
    return (arr || [])
      .map((b) => {
        const id =
          b.fld_branchid ??
          b.fld_Branchid ??
          b.fld_branchId ??
          b.fld_BranchId ??
          b.id ??
          b.value ??
          b.Value;

        const name =
          b.fld_branchname ??
          b.fld_BranchName ??
          b.fld_branchName ??
          b.name ??
          b.text ??
          b.Text;

        return { id: id != null ? String(id) : "", name: String(name || "") };
      })
      .filter((x) => x.id && x.name);
  };

  const loadMeta = async () => {
    if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");
    if (!String(branchId).trim()) return toast.warn("branchId missing.");

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/employee-transfer-approval/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchType || "HO"),
        },
        headers: axiosHeaders(),
        timeout: 60000,
      });

      if (res?.data?.success) {
        const fs = normalizeBranches(res.data.fromSites || []);
        const ts = normalizeBranches(res.data.toSites || []);
        setFromSites([{ id: "Select", name: "Select" }, ...fs]);
        setToSites([{ id: "Select", name: "Select" }, ...ts]);

        setStatuses(res.data.statuses || []);
        setDefaultStatus(res.data.defaultStatus || "Waiting");
        setStatus(res.data.defaultStatus || "Waiting");

        if (branchType !== "HO") {
          setFromSite(String(branchId));
        } else {
          setFromSite("Select");
        }

        setToSite("Select");
      } else {
        toast.error(res?.data?.message || "Meta load failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta load failed");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!String(finYearId).trim()) return toast.warn("Please select Financial Year.");
    if (!String(branchId).trim()) return toast.warn("branchId missing.");

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/employee-transfer-approval/list`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchType || "HO"),
          fromSite,
          toSite,
          empCode: empCode.trim(),
          empName: empName.trim(),
          fromDate: fromDate.trim(),
          toDate: toDate.trim(),
          status: status || defaultStatus,
          page: p,
          pageSize,
        },
        headers: axiosHeaders(),
        timeout: 60000,
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);

        setSelectedEmpIds([]);
        setSelectAll(false);
      } else {
        toast.error(res?.data?.message || "List load failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "List load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta().then(() => fetchList(1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const toggleSelect = (empid, approvedFlag) => {
    if (approvedFlag) return;
    const id = Number(empid);
    if (!id) return;

    setSelectedEmpIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmpIds([]);
      setSelectAll(false);
      return;
    }
    const ids = (rows || [])
      .filter((r) => String(r.approve) !== "True" && r.approve !== true && r.approve !== 1)
      .map((r) => Number(r.empid))
      .filter((n) => Number.isFinite(n) && n > 0);

    setSelectedEmpIds(ids);
    setSelectAll(true);
  };

  // Actual approve logic
  const performApprove = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${BASE_URL}/api/payroll/employee-transfer-approval/approve`,
        {
          finYearId: Number(finYearId),
          employeeIds: selectedEmpIds,
        },
        {
          headers: axiosHeaders(),
          timeout: 60000,
        }
      );

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Successfully Approved...");
        await fetchList(page);
      } else {
        toast.error(res?.data?.message || "Approve failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Approve failed");
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const handleApprove = () => {
    if (selectedEmpIds.length === 0) return toast.warn("Please select any one");
    setShowConfirm(true);
  };

  const openApprovalEntry = (employeeTransferId, empid) => {
    navigate("/PayRoll/EmployeeTransferEntry", {
      state: { employeeTransferId: employeeTransferId },
    });
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE TRANSFER APPROVAL LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Header row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Transfer Approval
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Search / Pagination / Approve
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleApprove} disabled={selectedEmpIds.length === 0} className={approveBtn}>
                <CheckCircle2 className="w-4 h-4" />
                Approve Selected
              </button>
            </div>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              <div>
                <label className={labelBase}>Employee Code</label>
                <input value={empCode} onChange={(e) => setEmpCode(e.target.value)} className={inputBase} placeholder="Type code..." />
              </div>

              <div>
                <label className={labelBase}>Employee Name</label>
                <input value={empName} onChange={(e) => setEmpName(e.target.value)} className={inputBase} placeholder="Type name..." />
              </div>

              <div>
                <label className={labelBase}>From Site</label>
                <select value={fromSite} onChange={(e) => setFromSite(e.target.value)} className={inputBase}>
                  {fromSites.map((b) => (
                    <option key={String(b.id)} value={String(b.id)}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>To Site</label>
                <select value={toSite} onChange={(e) => setToSite(e.target.value)} className={inputBase}>
                  {toSites.map((b) => (
                    <option key={String(b.id)} value={String(b.id)}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelBase}>From Date (dd/MM/yyyy)</label>
                <input value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputBase} placeholder="dd/MM/yyyy" />
              </div>

              <div>
                <label className={labelBase}>To Date (dd/MM/yyyy)</label>
                <input value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputBase} placeholder="dd/MM/yyyy" />
              </div>

              <div>
                <label className={labelBase}>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputBase}>
                  {(statuses || []).map((s) => (
                    <option key={s.value} value={s.value}>{s.text}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <button type="submit" className={primaryBtn} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Transfer Number</th>
                    <th className="px-4 py-4 text-center font-bold">Transfer Date</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Department</th>
                    <th className="px-4 py-4 text-center font-bold">From Site</th>
                    <th className="px-4 py-4 text-center font-bold">To Site</th>
                    <th className="px-4 py-4 text-center font-bold">Transfer Type</th>
                    <th className="px-4 py-4 text-center font-bold">Status</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 11 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const approved = String(r.approve) === "True" || r.approve === true || r.approve === 1;
                      return (
                        <tr key={String(r.fld_employee_transferemployeeid)} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                          <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_employee_transferemployeecode}
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.trdate}
                          </td>

                          <td className="px-4 py-3 text-sm text-left">
                            <button
                              type="button"
                              onClick={() => openApprovalEntry(r.fld_employee_transferemployeeid, r.empid)}
                              className={`font-bold underline ${isDark ? "text-blue-300" : "text-blue-700"}`}
                            >
                              {r.code}
                            </button>
                          </td>

                          <td className={`px-4 py-3 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.empname}
                          </td>

                          <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.depnme}
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.frombrnme}
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.brnme}
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.tpe}
                          </td>
                          <td className={`px-4 py-3 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.status}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedEmpIds.includes(Number(r.empid))}
                              disabled={approved}
                              onChange={() => toggleSelect(r.empid, approved)}
                              className="w-4 h-4"
                              title={approved ? "Already Approved" : "Select to approve"}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className={ghostBtn}
              >
                <ChevronLeft className="w-4 h-4" />
                Prev
              </button>

              <div className={`px-4 py-2 rounded-2xl font-bold ${isDark ? "bg-white/10 text-white" : "bg-gray-100 text-gray-800"}`}>
                {page} / {totalPages}
              </div>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className={ghostBtn}
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            className={`w-full max-w-md rounded-3xl border shadow-2xl p-8 ${
              isDark
                ? "bg-gray-800 border-white/10 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <div className="flex flex-col items-center text-center">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-5 ${
                isDark ? "bg-amber-500/20" : "bg-amber-100"
              }`}>
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>

              <h3 className="text-2xl font-bold mb-2">Confirm Approval</h3>
              <p className={`text-sm mb-8 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Are you sure you want to approve the selected{" "}
                <span className="font-bold text-emerald-500">{selectedEmpIds.length}</span>{" "}
                employee transfer{selectedEmpIds.length > 1 ? "s" : ""}?
              </p>

              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowConfirm(false)}
                  className={ghostBtn + " flex-1"}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={performApprove}
                  disabled={loading}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg bg-emerald-600 hover:bg-emerald-700 hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60"
                >
                  {loading ? "Approving..." : "Yes, Approve"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PayRollLayoutWrapper>
  );
};

export default EmployeeTransferApprovalListing;